import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";

const money = (value) =>
  Number(value || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatTimestamp = (value) => {
  try {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("en-AE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const shortCompany = (company) => {
  const text = String(company || "").toLowerCase();
  if (text.includes("comfort")) return "Comfort Line";
  if (text.includes("lolia")) return "Lolia Limousine";
  return company || "—";
};

export async function deleteDailyRideHailingUpload(upload) {
  const reportDate = String(upload?.reportDate || "").trim();
  const platform = String(upload?.platform || "").toLowerCase().trim();
  const companyKey = String(upload?.companyKey || "").trim();
  const uploadKey = String(upload?.uploadKey || upload?.id || "").trim();

  if (!reportDate || !platform || !companyKey || !uploadKey) {
    throw new Error("Upload history record is incomplete and cannot be deleted safely.");
  }

  const recordsRef = collection(db, "dailyRideHailingEarnings", reportDate, "records");
  const recordsSnap = await getDocs(recordsRef);
  const matching = recordsSnap.docs.filter((recordDoc) => {
    const data = recordDoc.data() || {};
    return (
      String(data.companyKey || "") === companyKey &&
      String(data.platform || "").toLowerCase() === platform &&
      // uploadKey is preferred. The company/platform fallback keeps old v1-v5
      // records deletable even if they pre-date uploadKey being relied upon.
      (!data.uploadKey || String(data.uploadKey) === uploadKey)
    );
  });

  // Current daily files contain far fewer than Firestore's per-batch limit.
  const batch = writeBatch(db);
  matching.forEach((recordDoc) => batch.delete(recordDoc.ref));
  await batch.commit();

  await deleteDoc(doc(db, "dailyRideHailingUploads", uploadKey));
  return matching.length;
}

export default function DailyRideHailingUploadHistory({ company = "", platform = "" }) {
  const [uploads, setUploads] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [deletingKey, setDeletingKey] = useState("");
  const [historyCompany, setHistoryCompany] = useState("all");
  const [historyPlatform, setHistoryPlatform] = useState("all");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "dailyRideHailingUploads"), (snap) => {
      setUploads(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (company) setHistoryCompany(company);
  }, [company]);

  useEffect(() => {
    if (platform) setHistoryPlatform(platform);
  }, [platform]);

  const visible = useMemo(() => {
    return [...uploads]
      .filter((u) => historyCompany === "all" || u.company === historyCompany || u.incomeCompany === historyCompany)
      .filter((u) => historyPlatform === "all" || String(u.platform || "").toLowerCase() === historyPlatform)
      .sort((a, b) => {
        const dateDiff = String(b.reportDate || "").localeCompare(String(a.reportDate || ""));
        if (dateDiff) return dateDiff;
        const aTime = a.updatedAt?.seconds || 0;
        const bTime = b.updatedAt?.seconds || 0;
        return bTime - aTime;
      });
  }, [uploads, historyCompany, historyPlatform]);

  async function removeUpload(upload) {
    const label = `${String(upload.platform || "").toUpperCase()} — ${shortCompany(upload.company)} — ${upload.reportDate}`;
    const ok = window.confirm(
      `Delete this daily upload?\n\n${label}\n${upload.sourceFileName || ""}\n\n` +
        `This removes ONLY the daily management-report data for this upload. Weekly Car Sheet data and driver mappings will not be changed.`
    );
    if (!ok) return;

    setDeletingKey(upload.uploadKey || upload.id);
    try {
      const removed = await deleteDailyRideHailingUpload(upload);
      alert(`Deleted daily upload and ${removed} saved driver row(s).`);
    } catch (err) {
      console.error(err);
      alert(`Delete failed: ${err?.message || err}`);
    } finally {
      setDeletingKey("");
    }
  }

  return (
    <div style={{ marginTop: 20 }}>
      <button
        type="button"
        onClick={() => setShowHistory((v) => !v)}
        style={historyButton}
      >
        {showHistory ? "Hide Upload History" : "View Upload History"}
      </button>

      {showHistory && (
        <div style={panel}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h3 style={{ margin: 0 }}>Daily Upload History</h3>
              <div style={{ marginTop: 4, color: "#64748b", fontSize: 14 }}>
                Check the report date and source filename before uploading again. Delete a wrong-date upload, then re-upload it with the correct date.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select value={historyCompany} onChange={(e) => setHistoryCompany(e.target.value)} style={filterInput}>
                <option value="all">All companies</option>
                <option value="Comfort Line Luxury Motor Vehicles Services LLC">Comfort Line</option>
                <option value="Lolia Limousine for Luxury Motor Vehicles Services LLC">Lolia Limousine</option>
              </select>
              <select value={historyPlatform} onChange={(e) => setHistoryPlatform(e.target.value)} style={filterInput}>
                <option value="all">All platforms</option>
                <option value="uber">Uber</option>
                <option value="bolt">Bolt</option>
              </select>
            </div>
          </div>

          <div style={{ overflowX: "auto", marginTop: 14 }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Report Date</th>
                  <th style={th}>Company</th>
                  <th style={th}>Platform</th>
                  <th style={th}>Source File</th>
                  <th style={{ ...th, textAlign: "right" }}>Rows</th>
                  <th style={{ ...th, textAlign: "right" }}>Bank</th>
                  <th style={{ ...th, textAlign: "right" }}>Cash</th>
                  <th style={th}>Uploaded / Updated</th>
                  <th style={th}>Uploaded By</th>
                  <th style={{ ...th, textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!visible.length && (
                  <tr>
                    <td colSpan={10} style={{ padding: 18, textAlign: "center", color: "#64748b" }}>
                      No daily uploads found for this filter.
                    </td>
                  </tr>
                )}
                {visible.map((u) => {
                  const key = u.uploadKey || u.id;
                  return (
                    <tr key={key}>
                      <td style={td}><strong>{u.reportDate || "—"}</strong></td>
                      <td style={td}>{shortCompany(u.company || u.incomeCompany)}</td>
                      <td style={td}>{String(u.platform || "").toUpperCase()}</td>
                      <td style={{ ...td, maxWidth: 310, wordBreak: "break-word" }}>{u.sourceFileName || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>{Number(u.rowCount || 0)}</td>
                      <td style={{ ...td, textAlign: "right" }}>AED {money(u.bankTotal)}</td>
                      <td style={{ ...td, textAlign: "right" }}>AED {money(u.cashTotal)}</td>
                      <td style={td}>{formatTimestamp(u.updatedAt)}</td>
                      <td style={td}>{u.uploadedBy || "—"}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => removeUpload(u)}
                          disabled={deletingKey === key}
                          style={deleteButton}
                        >
                          {deletingKey === key ? "Deleting..." : "Delete"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const historyButton = {
  padding: "10px 16px",
  border: "1px solid #475569",
  borderRadius: 6,
  background: "#fff",
  color: "#0f172a",
  fontWeight: 700,
  cursor: "pointer",
};

const panel = {
  marginTop: 12,
  padding: 16,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  background: "#fff",
};

const filterInput = {
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  background: "white",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const th = {
  padding: "9px 8px",
  background: "#f1f5f9",
  color: "#334155",
  borderBottom: "1px solid #cbd5e1",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const td = {
  padding: "9px 8px",
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
  whiteSpace: "nowrap",
};

const deleteButton = {
  padding: "6px 10px",
  border: "1px solid #dc2626",
  borderRadius: 5,
  background: "#fff",
  color: "#b91c1c",
  fontWeight: 700,
  cursor: "pointer",
};
